/**
 * Start the session check and the initial route's known GETs while code loads.
 * Home starts its agenda, Patients and Lists start their overviews, and Calendar
 * starts its current window. Clinical Trials reads its existing team List and
 * header data without creating a List or starting a sync. Docs starts its library query. Email starts its private inbox and an
 * explicitly selected message.
 * The backend authorizes each stored-token request. Failed early reads fall back
 * to the view's ordinary request after authentication resolves.
 */
import { dashboardOverviewOpeningRequest } from '@/app/dashboards/lib/overviewOpening';
import { agendaFetchWindow } from '@/app/apps/hooks/workspaceAgendaCache';
import { getApiBaseUrl } from './url-resolver';
import { LIST_OVERVIEW_REQUESTS } from '@/platform/performance/listOverviewRequests';
import { clinicalTrialsOpeningRequests } from '@/app/clinical-trials/lib/clinicalTrialsOpeningRequests';
import { myStudiesUserKey } from '@/app/clinical-trials/lib/myStudiesListCache';
import { startCalendarBootReads } from '@/app/calendar/lib/calendarOpeningData';
import { emailInboxBootRequests } from '@/app/email/lib/emailOpeningRequests';
import { workspaceHeaders } from '@rudi/api/workspace';
import { storedWorkspaceSlug, workspaceTenantIdForPath } from './workspaceSelection';

const TOKEN_STORAGE_KEY = 'rudi_auth_tokens';
const HOME_SEGMENT = 'home';

/** The request the Home agenda sends for the window of `now`. */
export interface HomeAgendaRequest {
  url: '/home/agenda';
  params: { time_min: string; time_max: string };
}

export function homeAgendaRequest(now: Date): HomeAgendaRequest {
  const { timeMin, timeMax } = agendaFetchWindow(now);
  return {
    url: '/home/agenda',
    params: { time_min: timeMin.toISOString(), time_max: timeMax.toISOString() },
  };
}

export interface StoredSession {
  accessToken: string;
  userId: string;
  tenantSlug: string | null;
  tenantId?: string | null;
}

interface BootRequest {
  token: string;
  response: Promise<Response>;
}

let authStatus: BootRequest | null = null;
let agenda: (BootRequest & { request: HomeAgendaRequest }) | null = null;
let overview: Array<BootRequest & { request: { url: string } }> = [];

export function readStoredSession(storage: Storage): StoredSession | null {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(TOKEN_STORAGE_KEY) ?? 'null');
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { accessToken, user } = parsed as {
      accessToken?: unknown;
      user?: { id?: unknown; tenantSlug?: unknown; tenantId?: unknown } | null;
    };
    if (typeof accessToken !== 'string' || !accessToken) return null;
    if (user?.id === undefined || user.id === null) return null;
    return {
      accessToken,
      userId: String(user.id),
      tenantSlug: typeof user.tenantSlug === 'string' && user.tenantSlug ? user.tenantSlug : null,
      tenantId: typeof user.tenantId === 'string' && user.tenantId ? user.tenantId : null,
    };
  } catch {
    return null;
  }
}

/**
 * Whether the URL opens the signed-in user's Home: `/`, their route prefix
 * (`/{tenant}/{userId}`, `/app/{userId}`), or that prefix plus `/home`. All
 * three land on Home; any other path opens another screen first.
 */
export function opensHome(pathname: string, session: StoredSession): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return true;
  const prefix = [session.tenantSlug ?? 'app', session.userId];
  if (segments.length < 2 || segments.length > 3) return false;
  if (segments[0] !== prefix[0] || segments[1] !== prefix[1]) return false;
  return segments.length === 2 || segments[2] === HOME_SEGMENT;
}

/** Whether the URL opens the signed-in user's Clinical Trials: `/{tenant}/{userId}/clinical-trials`. */
export function opensClinicalTrials(pathname: string, session: StoredSession): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return (
    segments.length === 3 &&
    segments[0] === (session.tenantSlug ?? 'app') &&
    segments[1] === session.userId &&
    segments[2] === 'clinical-trials'
  );
}

/**
 * Whether the URL opens the signed-in application (its shell), rather than
 * the login form or a public page: `/`, `/app/...` or the user's tenant.
 */
export function storedSessionOpensApp(
  pathname: string,
  storage: Storage = window.localStorage
): boolean {
  const session = readStoredSession(storage);
  if (session === null) return false;
  const [first] = pathname.split('/').filter(Boolean);
  return (
    first === undefined ||
    first === 'app' ||
    first === session.tenantSlug ||
    storedWorkspaceSlug(first, storage)
  );
}

/** Whether the session stored now (as after a sign-in) opens Home at this path. */
export function storedSessionOpensHome(
  pathname: string,
  storage: Storage = window.localStorage
): boolean {
  const session = readStoredSession(storage);
  return session !== null && opensHome(pathname, session);
}

// The workspace this page load works in (ADR 0113); set by startBootPrefetch.
let bootWorkspace: string | null = null;

function authorizedGet(url: string, token: string): Promise<Response> {
  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...workspaceHeaders(bootWorkspace),
    },
  });
}

/**
 * Start the session check and known first-screen reads with the stored token.
 * Does nothing without a stored session. Never throws.
 */
export function startBootPrefetch(
  location: Pick<Location, 'pathname'> & Partial<Pick<Location, 'search'>> = window.location,
  storage: Storage = window.localStorage,
  now: Date = new Date()
): void {
  overview = [];
  try {
    const session = readStoredSession(storage);
    if (!session) return;
    bootWorkspace = workspaceTenantIdForPath(location.pathname, storage);
    const apiBase = `${getApiBaseUrl()}/api`;
    const token = session.accessToken;
    authStatus = { token, response: authorizedGet(`${apiBase}/auth/status`, token) };
    // A rejected fetch nobody takes must not surface as an unhandled rejection.
    authStatus.response.catch(() => undefined);
    const segments = location.pathname.split('/').filter(Boolean);
    const app = segments[2];
    if (
      segments.length === 3 &&
      segments[0] === (session.tenantSlug ?? 'app') &&
      segments[1] === session.userId &&
      (app === 'patients' || app === 'lists' || (app === 'calendar' && !location.search))
    ) {
      overview =
        app === 'calendar'
          ? startCalendarBootReads(now, url => authorizedGet(`${apiBase}${url}`, token)).map(
              started => ({ ...started, token })
            )
          : LIST_OVERVIEW_REQUESTS[app].map(url => {
              const response = authorizedGet(`${apiBase}${url}`, token);
              response.catch(() => undefined);
              return { token, request: { url }, response };
            });
    }
    if (opensClinicalTrials(location.pathname, session) && !location.search && session.tenantId) {
      const userKey = myStudiesUserKey(session.tenantId, session.userId);
      for (const url of clinicalTrialsOpeningRequests(userKey, storage)) {
        const response = authorizedGet(`${apiBase}${url}`, token);
        response.catch(() => undefined);
        overview.push({ token, request: { url }, response });
      }
    }
    if (
      segments.length === 3 &&
      segments[0] === (session.tenantSlug ?? 'app') &&
      segments[1] === session.userId &&
      (app === 'docs' || app === 'dashboards') &&
      !location.search
    ) {
      const url =
        app === 'docs'
          ? '/docs/library?doc_type=document&limit=100&offset=0'
          : dashboardOverviewOpeningRequest();
      const response = authorizedGet(`${apiBase}${url}`, token);
      response.catch(() => undefined);
      overview = [{ token, request: { url }, response }];
    }
    if (
      segments[0] === (session.tenantSlug ?? 'app') &&
      segments[1] === session.userId &&
      app === 'email'
    ) {
      overview = emailInboxBootRequests(segments.slice(3), location.search ?? '').map(url => {
        const response = authorizedGet(`${apiBase}${url}`, token);
        response.catch(() => undefined);
        return { token, request: { url }, response };
      });
    }
    if (!opensHome(location.pathname, session)) return;
    const request = homeAgendaRequest(now);
    const query = new URLSearchParams(request.params).toString();
    agenda = {
      token,
      request,
      response: authorizedGet(`${apiBase}${request.url}?${query}`, token),
    };
    agenda.response.catch(() => undefined);
  } catch {
    // Storage or fetch unavailable: the application sends its own requests.
  }
}

/** The early `/auth/status` response for this token, once. */
export function takeBootAuthStatus(token: string): Promise<Response> | null {
  const started = authStatus;
  authStatus = null;
  return started && started.token === token ? started.response : null;
}

/** The early agenda request and its response, once. */
export function takeBootAgenda(): {
  request: HomeAgendaRequest;
  response: Promise<Response>;
} | null {
  const started = agenda;
  agenda = null;
  return started ? { request: started.request, response: started.response } : null;
}

/** Hand over first-screen reads once, only while their stored session still matches. */
export function takeBootOverview(storage?: Storage): Array<{
  request: { url: string };
  response: Promise<Response>;
}> {
  const started = overview;
  overview = [];
  try {
    const token = readStoredSession(storage ?? window.localStorage)?.accessToken;
    return started.filter(request => request.token === token);
  } catch {
    return [];
  }
}

/** Forget the requests started on this page load, for tests. */
export function resetBootPrefetchForTests(): void {
  authStatus = null;
  agenda = null;
  overview = [];
}
